import { useCallback, useState } from 'react'
import { itemId, useAdmin, type ListItem, type ListKey } from '../AdminContext'

/**
 * Gedeelde logica voor elke editor (product, protocol, behandeling, artikel):
 * - "nieuw" werkt op een lokaal concept tot je op Toevoegen klikt;
 * - een bestaand item past direct het concept van het beheer aan.
 * `item` is undefined als het item niet bestaat.
 *
 * `set` werkt altijd op de nieuwste stand (functionele update), niet op het item van de
 * vorige render. Twee wijzigingen in één handler gaan zo niet verloren, en een "Ongedaan
 * maken" van seconden later draait alleen zijn eigen velden terug.
 */
export function useEditorItem<K extends ListKey>(key: K, routeId: string, empty: () => ListItem<K>) {
  const { catalog, patchItem } = useAdmin()
  const isNew = routeId === 'nieuw'
  const [local, setLocal] = useState<ListItem<K>>(empty)
  const existing = (catalog[key] as ListItem<K>[]).find((x) => itemId(key, x) === routeId)
  const item = isNew ? local : existing
  const id = existing ? itemId(key, existing) : routeId

  const set = useCallback(
    (patch: Partial<ListItem<K>>) => {
      if (isNew) setLocal((prev) => ({ ...prev, ...patch }) as ListItem<K>)
      else patchItem(key, id, patch)
    },
    [isNew, key, id, patchItem],
  )

  return { isNew, item, set }
}
